import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  CompactTableCell,
  CompactTableRow,
  GridContainer,
  GridItem,
  PaddedTableCell,
} from '../ui-extension';
import { camelCaseReplace } from '../../../helpers/stringHelpers';
import { timeScaleRangeTypes } from '../../../constants/date-types';
import { getDailyDateRange } from '../../../helpers/date-helper';
import ConfirmDialog from '../../../shared/confirm-dialog';
import { LabeledInput } from '../labeled-input';
import { LoadingButton } from '@mui/lab';

import RefreshIcon from '@mui/icons-material/Refresh';
import { sortByProperty } from './tableHelpers';
import { MappedRow } from './tableComponents';

const DynamicTable = (props) => {
  const {
    rows = [{}],
    /** \\\\\\\\\ ROWS PROP SCHEMA \\\\\\\\\\\\
     * rows: [
     *  {
     * (BASIC KEY VALUE PAIR)
     * fieldName: value, - regular kvp
     *
     * (BUTTON INPUT)
     * fieldName: {
     *  button: true
     *  buttonTitle: 'Button Title',
     *  disabled?: boolean, - whether it is disabled or not
     *  value: boolean - don't really know if this is necessary
     *  endIcon:
     *  fieldName: value - this is the value your are passing to the parent component,
     * },
     *
     * (TOOL TIP)
     * fieldName: {
     * toolTip: true,
     * toolTipTitle: 'Tool Tip Title',
     * rows: [ - values for bottom placement drawer on row click
     *  {fieldName: value},
     *   {fieldName: value},
     *    ],
     * },
     *
     * (ACCORDION)
     * fieldName: {
     *  accordion: true,
     *  textColor: 'black'
     *  hoverText: string
     *  accordionTitle: 'Accordion Title',
     *  rows: [ - values for expanding bottom drop down nested rows on row click
     *    {fieldName: value},
     *    {fieldName: value},
     *  ],
     * },
     *
     * (DROPDOWN SELECT INPUT)
     * fieldName: {
     * dropDownSelect: true,
     * options: [
     *  {label: 'Option 1', value: 'option1'},
     *  {label: 'Option 2', value: 'option2'},
     *    ],
     * value: 'option1', - this is the value you are add to the row fieldName.value
     * setRowKey : ['key', ...] - specify the row level keys you want to apply this value to
     * accordionKey: 'key' - if you want to update sub rows, specify which key is the accordion
     * setSubRowKey: ['key', ...] - specify the sub row level keys you want to apply this value to
     * saveKey: 'key' - specify the name of the save key you want to trigger when making a change
     * },
     *
     * (CHECKBOX INPUT)
     * fieldName: {
     *  checkBox: true,
     *  value: true, - this is the value you are add to the row fieldName.value
     *  label: string
     *  title: string
     *  setRowKey : ['key', ...] - specify the row level keys you want to apply this value to
     * accordionKey: 'key' - if you want to update sub rows, specify which key is the accordion
     * setSubRowKey: ['key', ...] - specify the sub row level keys you want to apply this value to
     * saveKey: 'key' - specify the name of the save key you want to trigger when making a change
     * },
     *
     * (INPUTS - DATE, mobile date, text, number)
     * fieldName: {
     * input: true,
     * type: 'text', - type of input, can be text, number, date, etc.
     * value: 'value', - this is the value you are add to the row fieldName.value
     * updatedValue: 'value', - this is the value you are updating in the row fieldName.value
     * setRowKey : ['key', ...] - specify the row level keys you want to apply this value to
     * accordionKey: 'key' - if you want to update sub rows, specify which key is the accordion
     * setSubRowKey: ['key', ...] - specify the sub row level keys you want to apply this value to
     * saveKey: 'key' - specify the name of the save key you want to trigger when making a change
     * },
     *
     * (STACK)
     * fieldName: { - stack columns for more room on the row
     * stack: true,
     * direction: 'row', - direction of the stack, can be row or column
     * rows: [
     * 'Row 1',
     * 'Row 2',
     * 'Row 3',
     * ],
     * },
     *
     * (ICON BUTTON)
     * fieldName: {
     * iconButton: true,
     * icon: 'edit', - icon to show in the button, can be edit, delete, save, add
     * value: 'value', - this is the value you are add to the row fieldName.value
     * updatedValue: 'value', - this is the value you are updating in the row fieldName.value
     * },
     *
     * (TEXT WITH COLOR)
     * fieldName: {
     * textWithColor: true,
     * value: 'value', - this is the value you are add to the row fieldName.value
     * color: 'red', - color of the text, can be any valid css color
     * },
     *
     * (ITEM IMAGE)
     * fieldName: {
     * itemImage: true, - this will show the item image in the row
     * itemId: 'itemId', - this is the item id to show the image for
     * },
     *
     * (ERP LINK)
     * fieldName: {
     * erpLink: true, -- p21 dynamic link
     * linkTitle: 'Link Text', - this is the text to show for the link
     * query:{
     *  fieldName: 'value', - this is the query params to pass to the erp link}
     * },
     * windowName: 'Window Name',
     * value: 'value', - this is the value to use for the link
     * hoverText: 'Hover Text', - this is the text to show on hover
     * }
     *
     * (LINK)
     * fieldName: {
     * link: true,
     * linkTitle: 'Link Text', - this is the text to show for the link
     * href: 'https://example.com', - this is the url to open when the link is clicked
     * erpWindowLink: { - p21 dynamic link with open in new window icon button // optional- formats as stack row with reg link next to erpLink
     *   windowName: 'Window Name',
     *   query: {
     *     fieldName: 'value',
     *   },
     * linkTitle: 'Link Text', - this is the text to show for the link
     * openWindowIcon: true, - this is the flag display the link as a window open icon // optional default format text
     * href: 'https://example.com', - this is the url to open when the link is clicked
     * value: 'value', - this is the value to use for the link
     * hoverText: 'Hover Text', - this is the text to show on hover
     * }
     * };
     */
    refreshRows = () => {},
    showRefreshButton = false,
    filterFields = [], // fields to filter out of the rows from the parent component
    tableTitle,
    setParentState = () => [], // set parent state for row button actions returns row field values
    loading = false,
    showDateButtonGroup = false,
    timeFrameTextFlag = false, // shows time frame text in the date button group
    handleChangeTimeScale = () => {}, // set time scale for parent component
    preSelectedDateRangeType = '', // allows date range control from parent component
    setPreselectedDateRangeType = () => {}, // allows date range control from parent component
    preSelectedDateRange = {}, // date range object to set from parent component
    setCustomDateRange = () => {}, // allows date range actions to parent component
    showDateRangeLoadText = false, // shows load bar when rows are loading
    onClose = () => {},
    showCloseButton = false,
    showFilter = false,
    tableSize = 'small',
    tableCompactRows = false,
    elevation = 0,
    showRowCount = false,
    buttonColor = 'primary',
    buttonText = 'primary',
    tableHeight = null,
    showTitleArrow = false,
    confirmRows = () => {}, // confirm rows action returns all rows
    confirmRowsButtonTitle = null,
    buttonHeight: rowButtonHeight = null,
    checkBoxText = null,
    showTableHeaderCheckBox = false,
    onCheckBoxClick = () => {}, // returns all rows and check box value
    rowFilterLayout = false, // the table filter will be a row layout instead of column
    checkBoxValue = false,
    showSort = false,
    tableHeadFontColor = 'black',
    showBackDrop = false,
  } = props;

  // --- Virtualization state/hooks ---
  const containerRef = React.useRef(null);
  const [scrollTop, setScrollTop] = React.useState(0);
  const [containerHeight, setContainerHeight] = React.useState(600);

  React.useEffect(() => {
    const handleResize = () => {
      if (containerRef.current)
        setContainerHeight(containerRef.current.clientHeight);
    };
    const ref = containerRef.current;
    if (ref) {
      setContainerHeight(ref.clientHeight);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [mappedRows, setMappedRows] = useState(rows);
  const [prevMappedRows, setPrevMappedRows] = useState(rows);
  const [dateSelectionHeaderFields, setDateSelectionHeaderFields] = useState(
    []
  );
  const [fieldSelectionHeaderFields, setFieldSelectionHeaderFields] = useState(
    []
  );
  const [nestedDataToolTip, setNestedDataToolTip] = useState({
    open: false,
    rows: [],
  });

  const [alignment, setAlignment] = useState(preSelectedDateRangeType || '');
  const [datePickerDialog, setDatePickerDialog] = useState({
    open: false,
    startDate: null,
    endDate: null,
  });
  const [sortPattern, setSortPattern] = useState({ pattern: true, key: '' });
  const [filter, setFilter] = useState({
    column: null,
    value: '',
    prevValue: '',
  });
  const [filterType, setFilterType] = useState({
    type: 'field',
  });
  const [filterDateDialog, setFilterDateDialog] = useState({
    open: false,
    startDate: null,
    endDate: null,
    type: null,
  });
  const [dateType, setDateType] = useState('');
  const atLeastOneRowHasValue = mappedRows[0]
    ? Object.values(mappedRows[0]).some((value) => value)
    : false;

  // Debounced filter effect for filter.value
  useEffect(() => {
    const handler = setTimeout(() => {
      handleFilterChange(filter.value);
    }, 500); // 500ms debounce
    return () => clearTimeout(handler);
  }, [filter.value]);

  useEffect(() => {
    // Update the mapped rows whenever the original rows change
    setMappedRows(rows);
    setPrevMappedRows(rows);
    setScrollTop(0);
    setRowHeights({});

    //Keep showing the filter after rows update
    if (filter.value) {
      handleFilterChange(filter.value);
    }
  }, [rows]);

  // --- Variable row heights virtualization ---
  const DEFAULT_ROW_HEIGHT = 90; // px
  const EXPANDED_ROW_HEIGHT = 180; // px (adjust as needed)
  const buffer = 2;
  const totalRows = mappedRows.length || 0;

  // Track per-row heights (id -> height)
  const [rowHeights, setRowHeights] = React.useState({});

  const openNestedDataPopUp = React.useCallback((rows) => {
    setNestedDataToolTip({
      open: true,
      rows: rows?.length ? rows : [rows] || [],
    });
  }, []);

  // Helper: get row height (expanded/collapsed)
  const getRowHeight = React.useCallback(
    (row) => {
      // Defensive: always return a positive number
      if (!row || typeof row !== 'object') return DEFAULT_ROW_HEIGHT;
      // Check for expanded accordion
      const expanded = Object.values(row).find(
        (v) => v && v.accordion && v.open
      );
      if (expanded) {
        const numAccordionRows = Array.isArray(expanded.rows)
          ? expanded.rows.length
          : 0;
        const expandedHeight =
          DEFAULT_ROW_HEIGHT +
          (numAccordionRows > 0 ? numAccordionRows * 52 : EXPANDED_ROW_HEIGHT);
        if (isNaN(expandedHeight) || expandedHeight <= 0) {
          console.warn('[DynamicTable] getRowHeight: Invalid expandedHeight', {
            row,
            expandedHeight,
          });
          return DEFAULT_ROW_HEIGHT;
        }
        return expandedHeight;
      }
      // Use tracked height if set
      if (row.id && rowHeights[row.id]) {
        const h = rowHeights[row.id];
        if (isNaN(h) || h <= 0) {
          console.warn(
            '[DynamicTable] getRowHeight: Invalid rowHeights value',
            { row, h }
          );
          return DEFAULT_ROW_HEIGHT;
        }
        return h;
      }
      // Fallback to default
      return DEFAULT_ROW_HEIGHT;
    },
    [rowHeights]
  );

  // Helper: update row height (call from MappedRow when expanded/collapsed)
  const updateRowHeight = React.useCallback((rowId, height) => {
    setRowHeights((prev) => {
      if (prev[rowId] === height) return prev;
      return { ...prev, [rowId]: height };
    });
  }, []);

  // Compute cumulative heights for all rows
  const cumulativeHeights = React.useMemo(() => {
    const arr = [0];
    for (let i = 0; i < totalRows; i++) {
      const row = mappedRows[i];
      arr.push(arr[i] + getRowHeight(row));
    }
    return arr; // arr[i] = sum of heights of rows 0..i-1
  }, [mappedRows, totalRows, getRowHeight]);

  // Find startIndex and endIndex based on scrollTop/containerHeight
  function findRowIndexForOffset(offset) {
    let low = 0,
      high = totalRows;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (cumulativeHeights[mid + 1] <= offset) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    return low;
  }

  let startIndex = Math.max(0, findRowIndexForOffset(scrollTop) - buffer);
  let endIndex = Math.min(
    totalRows,
    findRowIndexForOffset(scrollTop + containerHeight) + buffer
  );
  // Clamp indices to valid range
  if (startIndex < 0 || isNaN(startIndex)) startIndex = 0;
  if (endIndex > totalRows || isNaN(endIndex)) endIndex = totalRows;
  if (startIndex > endIndex) {
    console.warn('[DynamicTable] startIndex > endIndex, resetting indices', {
      startIndex,
      endIndex,
      totalRows,
    });
    startIndex = 0;
    endIndex = Math.min(buffer, totalRows);
  }
  // Defensive: never allow negative or out-of-bounds
  startIndex = Math.max(0, Math.min(startIndex, totalRows));
  endIndex = Math.max(startIndex, Math.min(endIndex, totalRows));
  const virtualVisibleRows = mappedRows.slice(startIndex, endIndex);

  if (virtualVisibleRows.length === 0 && totalRows > 0) {
    console.error(
      '[DynamicTable] No visible rows rendered! Check virtualization math.',
      { startIndex, endIndex, totalRows, mappedRows }
    );
  }

  // Calculate top/bottom padding heights
  const topPaddingHeight = cumulativeHeights[startIndex];
  const bottomPaddingHeight =
    cumulativeHeights[totalRows] - cumulativeHeights[endIndex];

  useEffect(() => {
    // ability to set the date range from parent component
    if (preSelectedDateRangeType && !filter?.value) {
      setAlignment(preSelectedDateRangeType);

      setMappedRows(rows);
    }

    if (preSelectedDateRangeType === 'custom' && !filter?.value) {
      setDatePickerDialog({
        open: false,
        startDate: preSelectedDateRange.startDate,
        endDate: preSelectedDateRange.endDate,
      });
    }
  }, [preSelectedDateRangeType]);

  useEffect(() => {
    // capitalize/camelCaseReplace non date type header column fields
    if (rows?.[0]) {
      const dateFields = dateHeaderFieldNames();
      setDateSelectionHeaderFields(dateFields);
      // determine which fields are button true, don't show these fields in the header
      const headerFields = Object.keys(rows?.[0]).filter((field) => {
        const value = rows?.[0]?.[field];
        const isDateInput =
          value?.type === 'date' ||
          dateFields.includes(camelCaseReplace(field));

        return (
          !dateFields.includes(camelCaseReplace(field)) &&
          !value?.button &&
          !value?.toolTip &&
          !value?.accordion &&
          !value?.checkBox &&
          !value?.iconButton &&
          !value?.button &&
          !isDateInput
        );
      });
      setFieldSelectionHeaderFields(headerFields);
    }
  }, [filterType.type, rows?.length]);

  const handleSetParentState = useCallback(
    (row, updatedValue = '', key = null) => {
      const fieldName = key || Object.keys(row)[0];

      const updatedRow = {
        ...row,
        [fieldName]: {
          ...row[fieldName],
          updatedValue: updatedValue ?? row[fieldName]?.value ?? '',
        },
      };

      const updateKeyValuePair = {
        key: fieldName,
        [fieldName]: {
          ...updatedRow[fieldName],
          value: updatedRow[fieldName]?.updatedValue || '',
        },
      };

      setParentState({
        row,
        updatedRow,
        updateKeyValuePair,
      });
    },
    []
  );

  const handleDateChange = (event, newAlignment) => {
    setAlignment(newAlignment);
    // daily
    const { startOfDay, endOfDay } = getDailyDateRange();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);

    const timeScale = {
      startDate: null,
      endDate: null,
    };

    if (newAlignment === 'custom') {
      setDatePickerDialog({ open: true, startDate: null, endDate: null });
      return;
    }

    switch (newAlignment) {
      case 'oneMonth':
        timeScale.startDate = fourWeeksAgo;
        timeScale.endDate = endOfDay;
        break;
      case 'threeMonths':
        timeScale.startDate = new Date();
        timeScale.startDate.setMonth(timeScale.startDate.getMonth() - 3);
        timeScale.endDate = endOfDay;
        break;
      case 'sixMonths':
        timeScale.startDate = new Date();
        timeScale.startDate.setMonth(timeScale.startDate.getMonth() - 6);
        timeScale.endDate = endOfDay;
        break;
      default:
        timeScale.startDate = startOfDay;
        timeScale.endDate = endOfDay;
        break;
    }
    handleChangeTimeScale(newAlignment, timeScale);
  };

  const filteredObjectKeys = Object.keys(mappedRows?.[0] || {}).filter(
    (key) => !filterFields.includes(key) && key
  );

  // Update Mapped rows
  const onRowChange = React.useCallback((updatedRow) => {
    // Keep the Previous Mapped Rows in sync for filtering reset
    setPrevMappedRows((prevRows) => {
      const rowIndex = prevRows.findIndex((row) => row.id === updatedRow.id);
      if (rowIndex === -1) return prevRows;
      const updatedRows = [...prevRows];
      updatedRows[rowIndex] = { ...updatedRows[rowIndex], ...updatedRow };
      return updatedRows;
    });

    setMappedRows((prevRows) => {
      const rowIndex = prevRows.findIndex((row) => row.id === updatedRow.id);
      if (rowIndex === -1) return prevRows;

      const updatedRows = [...prevRows];
      updatedRows[rowIndex] = { ...updatedRows[rowIndex], ...updatedRow };
      return updatedRows;
    });
  }, []);

  const handleFilterChange = (filterValue) => {
    // Always keep a copy of the previous mappedRows before filtering
    if (filterValue === '') {
      if (prevMappedRows && prevMappedRows.length) {
        setMappedRows(prevMappedRows);
      }
      return;
    }

    let filteredRows = [];

    if (filter.column) {
      // only search the field we are filtering on
      filteredRows = prevMappedRows.filter((row) => {
        if (typeof row[filter.column] === 'object') {
          return row[filter.column]?.rows?.length && row[filter.column]?.stack
            ? row[filter.column]?.rows
                .map((item) => item.toLowerCase())
                .join(' ')
                ?.includes(filterValue?.toLowerCase())
            : typeof row[filter.column]?.value === 'string'
            ? row[filter.column]?.value
                .toLowerCase()
                .includes(filterValue?.toLowerCase() || '') // Text Sort
            : typeof row[filter.column]?.value === 'number' &&
              filterValue !== '' &&
              !isNaN(filterValue)
            ? row[filter.column]?.value
                .toString()
                .includes(filterValue.toString()) // Number substring match
            : false;
        } else if (typeof row[filter.column] === 'number') {
          return row[filter.column]
            ?.toString()
            ?.includes(filterValue?.toString());
        } else if (!row[filter.column]) {
          return false;
        }
        return typeof row[filter.column] === 'string'
          ? row[filter.column]
              ?.toLowerCase()
              ?.includes(filterValue?.toLowerCase())
          : row[filter.column] === filterValue;
      });
    }
    //No Column Filter was selected
    else {
      filteredRows = prevMappedRows.filter((row) => {
        if (typeof row === 'object') {
          return Object.values(row).some((value) => {
            if (typeof value === 'string') {
              return value.toLowerCase().includes(filterValue.toLowerCase());
            } else if (typeof value === 'number') {
              return value.toString().includes(filterValue.toString());
            } else if (typeof value === 'object') {
              return JSON.stringify(value)
                .toLowerCase()
                .includes(filterValue.toString().toLowerCase());
            }
          });
        } else {
          if (typeof row === 'string') {
            return row.toLowerCase().includes(filterValue.toLowerCase());
          } else if (typeof row === 'number') {
            return row.toString().includes(filterValue.toString());
          }
        }
      });
    }

    setMappedRows(filteredRows);
  };

  const dateHeaderFieldNames = () => {
    // find the date values in the row object
    if (!mappedRows?.[0]) return [];
    const row = Object.values(mappedRows?.[0]).find((value) => {
      return (
        (value &&
          typeof value !== 'number' &&
          !isNaN(new Date(value)) &&
          !value.button &&
          !value.toolTip) ||
        value?.type === 'date'
      );
    });
    if (!row) return [];
    // set the dateFields keys to select list
    const dateFields = Object.keys(mappedRows?.[0]).filter((key) => {
      const value = mappedRows?.[0]?.[key];
      return (
        (value &&
          typeof value !== 'number' &&
          !isNaN(new Date(value)) &&
          !value.button &&
          !value.toolTip) ||
        value?.type === 'date'
      );
    });

    return dateFields.map((field) => camelCaseReplace(field));
  };

  const handleConfirmDateFilter = () => {
    if (filterDateDialog.startDate && filterDateDialog.endDate) {
      // reverse the camel case
      const tmpFieldName = filter.column
        .split(' ')
        .map((word) => {
          return word.charAt(0).toLowerCase() + word.slice(1);
        })
        .join('');

      // find the actual field name
      const fieldName = Object.keys(mappedRows?.[0]).find((key) => {
        return key.toLowerCase() === tmpFieldName;
      });

      if (!fieldName) return;

      // set rows to date range
      const filteredRows = mappedRows.filter((row) => {
        const dateValue = new Date(row[fieldName]?.value || row[fieldName]);
        const startDate = new Date(filterDateDialog.startDate);
        const endDate = new Date(filterDateDialog.endDate);
        return dateValue >= startDate && dateValue <= endDate;
      });

      setMappedRows(filteredRows);
      setFilterDateDialog({ ...filterDateDialog, open: false });
    } else {
      setFilterDateDialog({ ...filterDateDialog, open: false });
    }
  };

  const selectFieldsToShow =
    filterType.type === 'date'
      ? dateSelectionHeaderFields
      : fieldSelectionHeaderFields;

  const resetState = () => {
    // Search all row properties for open and set to false
    const resetRows = rows.map((row) => {
      Object.keys(row).forEach((key) => {
        if (row[key]?.accordion && row[key]?.open) {
          row[key].open = false;
        }
      });
      return row;
    });

    setMappedRows(resetRows);
    setFilter({ column: null, value: '' });
    setFilterDateDialog({ open: false, startDate: null, endDate: null });
    setDatePickerDialog({ open: false, startDate: null, endDate: null });
    setAlignment(timeScaleRangeTypes.oneMonth);
    setDateType('');
    setSortPattern({ pattern: true, key: '' });
    onClose();
    setRowHeights({});
  };

  const clearDateSelection = () => {
    setFilterDateDialog({
      open: false,
      startDate: null,
      endDate: null,
      type: '',
    });
    setDateType('');
    setMappedRows(prevMappedRows);
  };

  return (
    <GridContainer sx={{ width: '100%' }}>
      {loading ? (
        <GridItem xs={12}>
          <Stack sx={{ width: '80%', mt: 1, ml: 1 }}>
            <Typography variant='body2' sx={{ fontSize: '25px' }}>
              Loading {tableTitle}
              {showDateButtonGroup && timeFrameTextFlag
                ? ` • ${camelCaseReplace(alignment)} • Time Frame...`
                : showDateButtonGroup && !timeFrameTextFlag
                ? ` ${camelCaseReplace(alignment)}`
                : '...'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {showDateRangeLoadText &&
              datePickerDialog.startDate &&
              datePickerDialog.endDate
                ? `Loading ${tableTitle} from ${datePickerDialog.startDate.toLocaleDateString()} to ${datePickerDialog.endDate.toLocaleDateString()}`
                : ''}
            </Typography>
            <LinearProgress color='primary' />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 2,
                height: '750px',
              }}
            ></Box>
          </Stack>
        </GridItem>
      ) : null}
      {!loading ? (
        <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
          <Stack
            direction='row'
            justifyContent={'space-between'}
            alignItems={'baseline'}
            spacing={0}
          >
            <Stack spacing={0.5} sx={{ ml: 1, mt: 0.25 }}>
              {showCloseButton ? (
                <Button
                  size='small'
                  variant='outlined'
                  endIcon={<HighlightOffIcon />}
                  onClick={() => resetState()}
                  sx={{
                    height: '22px',
                    fontSize: '13px',
                    width: '80px',
                    color: 'text.secondary',
                    borderColor: 'text.secondary',
                  }}
                >
                  Close
                </Button>
              ) : null}
              <Stack direction='row' spacing={1}>
                <Stack
                  sx={showCloseButton ? { mt: 1 } : {}}
                  direction='row'
                  spacing={2.25}
                  alignItems={'center'}
                >
                  {tableTitle && (
                    <Typography variant='body1' sx={{ fontSize: '25px' }}>
                      {tableTitle}
                    </Typography>
                  )}
                  {showRefreshButton ? (
                    <Button
                      endIcon={<RefreshIcon />}
                      onClick={() => {
                        resetState();
                        refreshRows();
                      }}
                    >
                      Refresh
                    </Button>
                  ) : null}
                  <Typography variant='body1' sx={{ fontSize: '25px' }}>
                    {tableTitle && showTitleArrow ? '⇓' : ''}
                  </Typography>
                  {confirmRowsButtonTitle ? (
                    <LoadingButton
                      size='small'
                      loading={loading}
                      variant='outlined'
                      sx={{ mb: 2 }}
                      onClick={() => confirmRows(mappedRows)}
                    >
                      {confirmRowsButtonTitle}
                    </LoadingButton>
                  ) : null}
                </Stack>
                {showTableHeaderCheckBox ? (
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems={'center'}
                    sx={{
                      border: 'rgb(199, 199, 199) 1px',
                      borderWidth: '1px',
                      p: 0.5,
                      borderStyle: 'solid',
                      borderRadius: '5px',
                    }}
                  >
                    <Typography
                      variant='body1'
                      sx={{ fontSize: '13px', color: 'text.secondary' }}
                    >
                      {checkBoxText}
                    </Typography>

                    <LabeledInput
                      type='checkbox'
                      value={checkBoxValue}
                      onChange={(e) => {
                        onCheckBoxClick(mappedRows, e.target.checked);
                      }}
                    />
                  </Stack>
                ) : null}
              </Stack>
              {alignment === 'custom' &&
              datePickerDialog.startDate &&
              datePickerDialog.endDate ? (
                <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
                  <Typography variant='caption'>{`Selected Custom Date Range: ${new Date(
                    datePickerDialog.startDate
                  ).toLocaleDateString()} - ${new Date(
                    datePickerDialog.endDate
                  ).toLocaleDateString()}`}</Typography>
                  <IconButton
                    size='small'
                    sx={{ height: '1px', color: 'text.secondary' }}
                    onClick={() => {
                      setDatePickerDialog({
                        open: false,
                        startDate: null,
                        endDate: null,
                      });
                      handleDateChange(null, timeScaleRangeTypes.oneMonth);
                    }}
                  >
                    <HighlightOffIcon />
                  </IconButton>
                </Stack>
              ) : null}
              <Stack direction='row' spacing={1}>
                {showDateButtonGroup ? (
                  <ToggleButtonGroup
                    color='primary'
                    value={alignment}
                    exclusive
                    onChange={(e, newAlignment) => {
                      setPreselectedDateRangeType(null);
                      setDatePickerDialog({
                        open: false,
                        startDate: null,
                        endDate: null,
                      });
                      handleDateChange(e, newAlignment);
                    }}
                    aria-label='Platform'
                    sx={{ mt: 1, height: '30px' }}
                  >
                    <ToggleButton value={timeScaleRangeTypes.oneMonth}>
                      Last Month
                    </ToggleButton>
                    <ToggleButton value={timeScaleRangeTypes.threeMonths}>
                      3 Months
                    </ToggleButton>
                    <ToggleButton value={timeScaleRangeTypes.sixMonths}>
                      6 Months
                    </ToggleButton>
                    <ToggleButton value={timeScaleRangeTypes.custom}>
                      Custom
                    </ToggleButton>
                  </ToggleButtonGroup>
                ) : null}
                {mappedRows.length ? (
                  <Paper
                    elevation={0}
                    sx={{ p: 0.75, backgroundColor: 'transparent' }}
                  >
                    <Typography
                      variant='caption'
                      sx={{ fontSize: '12px', m: 'auto' }}
                    >
                      {showRowCount ? `${mappedRows.length} rows` : ''}
                    </Typography>
                  </Paper>
                ) : null}
              </Stack>
            </Stack>
            {showFilter ? (
              <Paper
                elevation={0}
                sx={{ p: 0.75, backgroundColor: 'transparent' }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction='row'
                    justifyContent={'space-between'}
                    alignItems={'baseline'}
                    spacing={1}
                  >
                    <Button
                      variant='outlined'
                      onClick={() => setFilterType({ type: 'field' })}
                      sx={{
                        backgroundColor:
                          filterType.type === 'field'
                            ? 'primary.main'
                            : 'transparent',
                        color:
                          filterType.type === 'field'
                            ? 'white'
                            : 'primary.main',
                        fontSize: '13px',
                        height: '20px',
                      }}
                    >
                      By Field
                    </Button>
                    <Button
                      onClick={() => setFilterType({ type: 'date' })}
                      sx={{
                        backgroundColor:
                          filterType.type === 'date'
                            ? 'primary.main'
                            : 'transparent',
                        color:
                          filterType.type === 'date' ? 'white' : 'primary.main',
                        cursor: 'pointer',
                        fontSize: '13px',
                        height: '20px',
                      }}
                      color='primary'
                    >
                      By Date
                    </Button>
                  </Stack>
                  <Stack
                    spacing={1}
                    direction={rowFilterLayout ? 'row' : 'column'}
                  >
                    <FormControl
                      variant='outlined'
                      size={rowFilterLayout ? 'medium' : 'small'}
                    >
                      <InputLabel
                        sx={rowFilterLayout ? { mb: 1.5 } : {}}
                        htmlFor='outlined-adornment-amount'
                      >
                        {'Column Filter'}{' '}
                      </InputLabel>

                      <Select
                        size={rowFilterLayout ? 'medium' : 'small'}
                        label='Column'
                        onChange={(event) => {
                          setFilter({ ...filter, column: event.target.value });
                          if (event.target.value === '') {
                            setMappedRows(prevMappedRows);
                            return;
                          }
                        }}
                        value={filter.column}
                        sx={{
                          height: rowFilterLayout ? '40px' : '35px',
                          width: '250px',
                          backgroundColor: 'white',
                        }}
                      >
                        <MenuItem value={null}>Clear Selection</MenuItem>
                        {selectFieldsToShow.map((field, index) => {
                          return (
                            <MenuItem key={index} value={field}>
                              {camelCaseReplace(field)}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <Paper
                      elevation={0}
                      sx={{
                        width: filterType.type === 'date' ? '250px' : '250px',
                        height: filterType.type === 'date' ? '35px' : '42px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '1px',
                      }}
                    >
                      {filterType.type === 'date' ? (
                        <Stack
                          spacing={0}
                          direction='row'
                          justifyContent={'space-between'}
                          sx={{
                            backgroundColor: 'transparent',
                            fontSize: '12px',
                          }}
                        >
                          <Button
                            variant='contained'
                            color={
                              dateType === 'between-dates' ? 'error' : 'primary'
                            }
                            disabled={!filter.column}
                            onClick={() => {
                              dateType !== 'between-dates'
                                ? setFilterDateDialog({
                                    open: true,
                                    startDate: null,
                                    endDate: null,
                                    type: 'between-dates',
                                  })
                                : clearDateSelection();
                              setDateType(
                                dateType === 'between-dates'
                                  ? ''
                                  : 'between-dates'
                              );
                            }}
                            size='small'
                            sx={{ fontSize: '12px' }}
                          >
                            {dateType === 'between-dates'
                              ? 'Clear Selection'
                              : 'Between Dates'}
                          </Button>

                          <Button
                            variant='contained'
                            disabled={!filter.column}
                            color={
                              dateType === 'after-date' ? 'error' : 'warning'
                            }
                            onClick={() => {
                              const { endOfDay } = getDailyDateRange();

                              if (dateType === 'after-date') {
                                setFilterDateDialog({
                                  open: false,
                                  startDate: null,
                                  endDate: null,
                                  type: '',
                                });
                                clearDateSelection();
                              } else {
                                setFilterDateDialog({
                                  open: true,
                                  startDate: null,
                                  endDate: endOfDay,
                                  type: 'after-date',
                                });
                                setDateType('after-date');
                              }
                            }}
                            size='small'
                            sx={{ fontSize: '12px' }}
                          >
                            {dateType === 'after-date'
                              ? 'Clear Selection'
                              : 'After Date'}
                          </Button>
                        </Stack>
                      ) : (
                        <FormControl variant='outlined' size='small'>
                          <InputLabel htmlFor='outlined-adornment-amount'>
                            Search Value Filter
                          </InputLabel>
                          <OutlinedInput
                            size='small'
                            label='Search Value'
                            endAdornment={
                              filter.value !== '' && (
                                <InputAdornment position='end'>
                                  <IconButton
                                    onClick={() => {
                                      setFilter({ ...filter, value: '' });
                                      setNestedDataToolTip({
                                        open: false,
                                        rows: [],
                                        title: '',
                                      });
                                      setMappedRows(prevMappedRows);
                                    }}
                                    edge='end'
                                  >
                                    <HighlightOffIcon
                                      fontSize='small'
                                      sx={{ color: 'text.secondary' }}
                                    />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                            // disabled={!filter.column}
                            sx={{
                              width: '250px',
                              height: '40px',
                              backgroundColor: 'white',
                              color: 'black',
                            }}
                            value={filter.value}
                            onChange={(event) => {
                              setFilter({
                                ...filter,
                                prevValue: filter.value,
                                value: event.target.value,
                              });
                            }}
                            type='text'
                          />
                        </FormControl>
                      )}
                    </Paper>
                  </Stack>
                </Stack>
              </Paper>
            ) : null}
          </Stack>
          {
            <>
              {atLeastOneRowHasValue ? (
                <Box sx={{ mb: 1, ml: 1 }}>
                  <Typography variant='caption' color='text.secondary'>
                    {`${mappedRows.length} row${
                      mappedRows.length !== 1 ? 's' : ''
                    }`}
                  </Typography>
                </Box>
              ) : null}

              <TableContainer
                component={Paper}
                ref={containerRef}
                onScroll={(e) => setScrollTop(e.target.scrollTop)}
                style={{
                  maxHeight: tableHeight || 800, // Fixed height for virtualization
                  height: tableHeight || 800,
                  overflow: 'auto',
                  willChange: 'transform, scroll-position',
                  scrollBehavior: 'auto',
                }}
              >
                <Backdrop
                  sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                  }}
                  open={showBackDrop}
                >
                  <CircularProgress color='inherit' />
                </Backdrop>

                <Table
                  size={tableSize}
                  aria-label='a dense table'
                  sx={{
                    mt: 1,
                    borderRadius: '5px',
                    position: 'relative',
                    marginTop: 0,
                    tableLayout: 'fixed', // Prevent columns from resizing
                  }}
                >
                  <TableHead>
                    {tableCompactRows ? (
                      <CompactTableRow sx={{ fontWeight: 'bold' }}>
                        {filteredObjectKeys?.map((key, index) => {
                          const colWidth = `${
                            100 / filteredObjectKeys.length
                          }%`;
                          const isActionField = mappedRows?.find(
                            (row) =>
                              row[key]?.button ||
                              row[key]?.toolTip ||
                              row[key]?.checkbox ||
                              row[key]?.iconButton ||
                              row[key]?.accordion
                          );
                          return (
                            <CompactTableCell
                              sx={{
                                backgroundColor: 'rgb(169, 171, 173)',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                fontWeight: 'bold',
                                textAlign: 'center',
                                width: colWidth,
                                minWidth: colWidth,
                                maxWidth: colWidth,
                              }}
                              key={index}
                            >
                              <Stack
                                direction='row'
                                alignItems={'center'}
                                spacing={0}
                              >
                                <Typography
                                  variant='caption'
                                  sx={{ fontSize: '10px', m: 'auto' }}
                                >
                                  {camelCaseReplace(key)}
                                </Typography>
                                {!isActionField && showSort && (
                                  <IconButton
                                    onClick={() => {
                                      setSortPattern({
                                        pattern: !sortPattern.pattern,
                                        key,
                                        isActionField: isActionField
                                          ? true
                                          : false,
                                      });
                                      setMappedRows(
                                        sortByProperty(
                                          [...mappedRows],
                                          key,
                                          sortPattern.pattern ? 'asc' : 'desc'
                                        )
                                      );
                                    }}
                                  >
                                    {sortPattern.key === key &&
                                    sortPattern.pattern ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )}
                                  </IconButton>
                                )}
                              </Stack>
                            </CompactTableCell>
                          );
                        })}
                      </CompactTableRow>
                    ) : (
                      <TableRow sx={{ backgroundColor: 'rgb(169, 171, 173)' }}>
                        {filteredObjectKeys?.map((key, index) => {
                          const isActionField = mappedRows?.find(
                            (row) =>
                              row[key]?.button ||
                              row[key]?.toolTip ||
                              row[key]?.iconButton ||
                              row[key]?.checkbox ||
                              row[key]?.accordion
                          );
                          return (
                            <PaddedTableCell
                              key={index}
                              sx={{
                                backgroundColor: 'rgb(169, 171, 173)',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                fontWeight: 'bold',
                                textAlign: 'center',
                              }}
                            >
                              <Stack
                                direction='row'
                                alignItems={'center'}
                                spacing={0}
                              >
                                <Typography
                                  variant='caption'
                                  sx={{
                                    fontSize: '11.5px',
                                    m: 'auto',
                                    fontWeight: 'bold',
                                    color: tableHeadFontColor,
                                  }}
                                >
                                  {camelCaseReplace(key)}
                                </Typography>
                                {!isActionField && showSort && (
                                  <IconButton
                                    size='small'
                                    onClick={() => {
                                      setSortPattern({
                                        pattern: !sortPattern.pattern,
                                        key,
                                        isActionField: isActionField
                                          ? true
                                          : false,
                                      });
                                      setMappedRows(
                                        sortByProperty(
                                          [...mappedRows],
                                          key,
                                          sortPattern.pattern ? 'asc' : 'desc'
                                        )
                                      );
                                    }}
                                  >
                                    {sortPattern.key === key &&
                                    sortPattern.pattern ? (
                                      <KeyboardArrowUpIcon />
                                    ) : (
                                      <KeyboardArrowDownIcon />
                                    )}
                                  </IconButton>
                                )}
                              </Stack>
                            </PaddedTableCell>
                          );
                        })}
                      </TableRow>
                    )}
                  </TableHead>
                  <TableBody>
                    {/* Virtualized visible rows with proper padding rows */}
                    {/* Always render top padding row for stable scroll height */}
                    <TableRow>
                      <TableCell
                        style={{
                          height: topPaddingHeight,
                          padding: 0,
                          border: 0,
                        }}
                        colSpan={filteredObjectKeys.length}
                      />
                    </TableRow>
                    {virtualVisibleRows.length > 0 ? (
                      virtualVisibleRows.map((row, idx) => (
                        <MappedRow
                          key={row.id || idx}
                          row={row}
                          index={startIndex + idx}
                          compact={tableCompactRows}
                          buttonColor={buttonColor}
                          buttonText={buttonText}
                          rowButtonHeight={rowButtonHeight}
                          onRowChange={onRowChange}
                          filterFields={filterFields}
                          handleSetParentState={handleSetParentState}
                          updateRowHeight={updateRowHeight}
                          getRowHeight={getRowHeight}
                          setNestedDataToolTip={setNestedDataToolTip}
                          openNestedDataPopUp={openNestedDataPopUp}
                        />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          align='center'
                          colSpan={filteredObjectKeys.length}
                        >
                          <Typography variant='body2'>
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {/* Always render bottom padding row for stable scroll height */}
                    <TableRow>
                      <TableCell
                        style={{
                          height: bottomPaddingHeight,
                          padding: 0,
                          border: 0,
                        }}
                        colSpan={filteredObjectKeys.length}
                      />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          }
          <Drawer
            elevation={elevation}
            anchor={'bottom'}
            open={nestedDataToolTip.open}
            onClose={() => setNestedDataToolTip({ open: false, rows: [] })}
          >
            <GridContainer spacing={1}>
              <GridItem xs={10.65} />
              <GridItem xs={1.25} sx={{ textAlign: 'right' }}>
                <IconButton
                  onClick={() =>
                    setNestedDataToolTip({ open: false, rows: [] })
                  }
                >
                  <ExpandMore sx={{ color: 'text.secondary' }} />
                </IconButton>
              </GridItem>
              <GridItem xs={12}>
                <Table size='small'>
                  <TableHead>
                    <CompactTableRow>
                      {nestedDataToolTip?.rows?.[0] &&
                        Object.keys(nestedDataToolTip?.rows?.[0])?.map(
                          (key, index) => (
                            <CompactTableCell
                              sx={{ textAlign: 'center', fontWeight: 'bold' }}
                              key={index}
                            >
                              {camelCaseReplace(key)}
                            </CompactTableCell>
                          )
                        )}
                    </CompactTableRow>
                  </TableHead>
                  {nestedDataToolTip?.rows?.length &&
                    nestedDataToolTip?.rows?.map((row, index) => (
                      <TableRow key={index}>
                        {row &&
                          Object.keys(row)?.map((key, index) => {
                            return (
                              <PaddedTableCell
                                key={index}
                                sx={{ textAlign: 'center' }}
                              >
                                {typeof row[key] === 'object' && row[key]
                                  ? row[key]?.value || ''
                                  : row[key]}
                              </PaddedTableCell>
                            );
                          })}
                      </TableRow>
                    ))}
                </Table>
              </GridItem>
            </GridContainer>
          </Drawer>
          <ConfirmDialog
            title='Select Custom Date Range'
            confirmText='Confirm Selected Dates'
            variant='outlined'
            cancelColor='warning'
            open={datePickerDialog.open}
            onConfirm={() => {
              setDatePickerDialog({ ...datePickerDialog, open: false }); // retain the date range for the UI
              if (datePickerDialog.startDate && datePickerDialog.endDate) {
                setCustomDateRange(
                  datePickerDialog.startDate,
                  datePickerDialog.endDate,
                  'drillDownListSelection',
                  alignment
                );
              }
            }}
            datePicker={true}
            setDates={(updatedDates) => {
              const { startDate, endDate } = updatedDates;
              setDatePickerDialog({ open: true, startDate, endDate });
            }}
            dates={{
              startDate: datePickerDialog.startDate,
              endDate: datePickerDialog.endDate,
            }}
            showCancel='true'
            onClose={() =>
              setDatePickerDialog({
                open: false,
                startDate: null,
                endDate: null,
              })
            }
          />
          <ConfirmDialog
            title='Select Date Range'
            confirmText='Confirm Selected Dates'
            variant='outlined'
            cancelColor='error'
            open={filterDateDialog.open}
            onConfirm={() => {
              handleConfirmDateFilter();
            }}
            datePicker={true}
            setDates={(updatedDates) => {
              const { startDate, endDate } = updatedDates;
              setFilterDateDialog({ open: true, startDate, endDate });
            }}
            dates={{
              startDate: filterDateDialog.startDate,
              endDate: filterDateDialog.endDate,
            }}
            showCancel='true'
            onClose={() =>
              setFilterDateDialog({
                open: false,
                startDate: null,
                endDate: null,
              })
            }
          />
        </GridItem>
      ) : null}
    </GridContainer>
  );
};

export default DynamicTable;
